import React, { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import UserAuthorityService from '../services/userAuthorityService';

/**
 * Custom hook to protect components from deleted users
 * Automatically checks if current user is deleted and redirects to login if they are
 * @param {Object} options - Configuration options
 * @param {boolean} options.checkOnMount - Check immediately when component mounts (default: false)
 * @param {boolean} options.showToast - Show error toast when deleted user is detected (default: true)
 * @param {string} options.redirectTo - Where to redirect deleted users (default: '/authentication/basic/login')
 * @param {Function} options.onDeleted - Custom callback when deleted user is detected
 * @returns {Function} Manual check function that can be called anytime
 */
export const useDeletedUserProtection = (options = {}) => {
  const history = useHistory();
  const hasChecked = useRef(false);
  const isChecking = useRef(false);

  const {
    checkOnMount = false, // Changed default to false to reduce automatic calls
    showToast = true,
    redirectTo = '/authentication/basic/login',
    onDeleted = null
  } = options;

  const checkDeletedUser = async () => {
    // Prevent multiple simultaneous checks
    if (isChecking.current) {
      return false;
    }

    try {
      isChecking.current = true;

      // First check if user is authenticated
      const currentUser = await UserAuthorityService.getCurrentUser();
      if (!currentUser) {
        isChecking.current = false;
        return false; // No user to check
      }

      const isDeleted = await UserAuthorityService.isDeleted();

      if (isDeleted) {
        // Sign out the user
        await UserAuthorityService.signOut();

        // Show toast notification (only once)
        if (showToast && !hasChecked.current) {
          toast.error('Your account has been deactivated. Please contact support for assistance.', {
            autoClose: 5000
          });
        }

        // Call custom callback if provided
        if (onDeleted && typeof onDeleted === 'function') {
          onDeleted();
        }

        // Redirect to login
        history.push(redirectTo);
        hasChecked.current = true;
        isChecking.current = false;
        return true; // User was deleted
      }

      isChecking.current = false;
      return false; // User is valid
    } catch (error) {
      console.error('Error checking if user is deleted:', error);
      isChecking.current = false;
      return false; // Assume valid if we can't check
    }
  };

  useEffect(() => {
    if (checkOnMount && !hasChecked.current && !isChecking.current) {
      // Add a longer delay to ensure auth state is fully settled
      const timer = setTimeout(checkDeletedUser, 1000);
      return () => clearTimeout(timer);
    }
  }, [checkOnMount]);

  return checkDeletedUser;
};

/**
 * Higher-order component that wraps a component with deleted user protection
 * @param {React.Component} WrappedComponent - Component to protect
 * @param {Object} options - Protection options
 * @returns {React.Component} Protected component
 */
export const withDeletedUserProtection = (WrappedComponent, options = {}) => {
  return function ProtectedComponent(props) {
    useDeletedUserProtection(options);
    return <WrappedComponent {...props} />;
  };
};

export default useDeletedUserProtection;
